import { FC, ReactNode, useEffect } from 'react';
import { TaskScope } from './components/TaskScope/TaskScope';
import { TaskConditions } from './components/TaskCondinions/TaskConditions';
import { TaskActions } from './components/TaskActions/TaskActions';
import { BackButton } from '@dynamika-shared/ui/BackButton/BackButton';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from '@components/Tooltip/Tooltip';
import {
  PMTaskDefinition,
  TaskDefinitionActionType,
} from '@dynamika-shared/types/pmTaskDefinitions';
import { FormProvider, useForm } from 'react-hook-form';
import { useBreadcrumbs } from '@hooks/useBreadcrumbs';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { useClinicalTrialInventorySlice } from '@restore/clinicalTrialInventory/clinicalTrialInventory.slice';
import { usePMTaskDefinitionSlice } from '@restore/PMTaskDefinition/PMTaskDefinition.slice';
import { Switch } from '@dynamika-shared/ui/Switch/Switch';
import { InputNumber } from '@dynamika-shared/ui/InputNumber/InputNumber';
import { EditableField } from '@components/EditableField/EditableField';

type EditState = {
  formType: 'edit';
  data: PMTaskDefinition;
};

type CreateState = {
  formType: 'create';
  data: PMTaskDefinition;
};

export type TaskState = EditState | CreateState;

type TaskDefinitionFormProps = {
  className?: string;
  children?: ReactNode;
};

export const TaskDefinitionForm: FC<TaskDefinitionFormProps> = () => {
  const { ctId } = useParams();

  const [clinicalTrial, fetchClinicalTrial] = useClinicalTrialSlice(
    (state, actions) => [state.data, actions.fetchClinicalTrial]
  );
  const [fetchClinicalTrialInventory] = useClinicalTrialInventorySlice(
    (_, actions) => [actions.fetchClinicalTrialUtilsInventory]
  );

  const [taskActions] = usePMTaskDefinitionSlice((_, actions) => [actions]);

  useBreadcrumbs(
    [
      ['Clinical Trials', 'clinicalTrials'],
      [clinicalTrial?.name, 'clinicalTrialGeneralInformation'],
      ['Task Definition', 'taskDefinition'],
    ],
    [ctId]
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { data, formType } = location.state as TaskState;

  const methods = useForm({ defaultValues: data });

  const { watch, setValue, getValues } = methods;

  const {
    description,
    name,
    actions,
    code,
    taskDefinitionOrder,
    automaticExecution,
    visibleForImagingSpecialist,
  } = watch();

  const addAction = () => {
    setValue('actions.actions', [
      ...actions.actions,
      {} as TaskDefinitionActionType,
    ]);
  };

  const createTaskDefinition = () => {
    taskActions.createPMTaskDefinition({
      clinicalTrialId: ctId as string,
      data: getValues(),
    });
  };
  const updateTaskDefinition = () => {
    taskActions.updatePMTaskDefinition({
      clinicalTrialId: ctId as string,
      taskDefinitionId: data.id,
      data: getValues(),
    });
  };

  useEffect(() => {
    if (ctId) {
      fetchClinicalTrial(ctId);
      fetchClinicalTrialInventory(ctId);
    }
  }, [ctId]);

  return (
    <div>
      <FormProvider {...methods}>
        <form>
          <header className='mini:py-2 flex w-full items-center justify-between py-4'>
            <div>
              <div className='header-info flex max-w-full items-center gap-[.5rem]'>
                <BackButton action={() => navigate(-1)} />
                <Tooltip
                  color='white'
                  title={<span className='text-1424 text-black'>{name}</span>}
                >
                  <div className={'flex  text-left'}>
                    <EditableField
                      name={name}
                      onChange={value => setValue('name', value)}
                      value={name}
                    >
                      <h2 className='mini:text-[2.4rem] mini:leading-[2.8] text-[3.6rem] font-semibold leading-[4.5rem]'>
                        {name}
                      </h2>
                    </EditableField>
                  </div>
                </Tooltip>
              </div>
              <div className='text-left ms-4 mini:-mt-2  mini:ms-3.5'>
                <EditableField
                  name={description}
                  onChange={value => setValue('description', value)}
                  value={description}
                >
                  <span className={'mini:text-1216 text-dimGray text-1624'}>
                    {description}
                  </span>
                </EditableField>
              </div>
              <div className='w-[30rem] ms-3 mb-1 mt-2 text-1424 flex items-center justify-between'>
                <div className={'me-1 font-semibold'}>Code:</div>
                {code}
              </div>
              <div className='w-[30rem] ms-3 mb-1 text-1424 flex items-center font-semibold justify-between'>
                <div className={'me-6 mt-1 whitespace-nowrap'}>
                  Task Definition Order:
                </div>
                <InputNumber
                  className={'w-7'}
                  value={taskDefinitionOrder}
                  onChange={value =>
                    setValue('taskDefinitionOrder', Number(value))
                  }
                  min={0}
                  controls
                />
              </div>
              <Switch
                className='w-[30rem] !ms-[2rem] mt-1'
                classNameLabel={'ms-3 flex justify-start'}
                checked={visibleForImagingSpecialist}
                onChange={e => setValue('visibleForImagingSpecialist', e)}
                name={'automaticExecution'}
                label={'Visible to Image specialist:'}
                inOneLine
                size={'small'}
              />
              <Switch
                className='w-[30rem] !ms-[6rem] mt-1'
                classNameLabel={'ms-3 mt-2.5 flex justify-start'}
                checked={automaticExecution}
                onChange={e => setValue('automaticExecution', e)}
                name={'automaticExecution'}
                label={'Automatic Execution:'}
                inOneLine
                size={'small'}
              />
            </div>
            <div className='header-action flex justify-center flex-nowrap self-baseline'>
              <Button
                type='primary'
                className='h-5 rounded-[12px]'
                onClick={
                  formType === 'create'
                    ? createTaskDefinition
                    : updateTaskDefinition
                }
              >
                Save & Close
              </Button>
            </div>
          </header>
          <div className='flex flex-col gap-3'>
            <TaskScope />
            <TaskConditions />
            <div>
              <h4 className='font-semibold text-[2.4rem] mb-3'>Actions</h4>
              <div className={'flex flex-col gap-3'}>
                {watch('actions.actions')?.map((_, i) => (
                  <TaskActions key={`action-${i}`} index={i} />
                ))}
              </div>
              <Button
                type={'text'}
                className={'text-1424 text-vividTangelo font-medium mt-3'}
                onClick={addAction}
              >
                + Add Action
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
